import { useState } from 'react';
import {
  NJBadge,
  NJIconButton,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { useNetworkData } from './hooks';
import type { VariableType } from './types';
import MapView from './MapView';
import Legend from './Legend';
import Toolbar from './Toolbar';
import NemoShell from './NemoShell';

export const meta = {
  title: 'Résultats cartographiques - V0 Mapbox',
  description: 'Visualisation cartographique des résultats d\'optimisation : réseau coloré par pression/température sur fond Mapbox.',
  tags: ['optimization', 'editor', 'reference'] as const,
  author: 'david.vankerckhove',
  status: 'draft' as const,
  category: 'optimisations/cartographic-results',
};

const TABS = [
  { id: 'parameters', label: 'Paramètres' },
  { id: 'cartographic-results', label: 'Résultats cartographiques' },
  { id: 'charts', label: 'Graphiques' },
  { id: 'synthesis', label: 'Synthèse' },
  { id: 'logs', label: 'Logs' },
];

export default function CartographicResults() {
  const { data, isLoading, isError } = useNetworkData();
  const [activeTab, setActiveTab] = useState(1);
  const [nodeVariable, setNodeVariable] = useState<VariableType>('pressure_bar');
  const [pipeVariable, setPipeVariable] = useState<VariableType>('pressure_bar');
  const [nodesVisible, setNodesVisible] = useState(true);
  const [pipesVisible, setPipesVisible] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const NODE_VAR_LABELS: Record<string, string> = { pressure_bar: 'Pression [bar]', temperature_c: 'Température [°C]', altitude: 'Altitude [m]', pressure_without_altitude_bar: 'Pression sans alt. [bar]', speed_flow: 'Vitesse [m/s]', net_flow: 'Débit [T/h]', delta_pressure_bar: 'DeltaP [bar]', gradient_loss: 'Gradient [Pa/m]', nominal_diameter: 'DN [m]' };
  const PIPE_VAR_LABELS: Record<string, string> = { pressure_bar: 'Pression moy. [bar]', temperature_c: 'Température moy. [°C]', pressure_without_altitude_bar: 'Pression sans alt. [bar]', speed_flow: 'Vitesse [m/s]', net_flow: 'Débit [T/h]', delta_pressure_bar: 'DeltaP [bar]', gradient_loss: 'Gradient [Pa/m]', nominal_diameter: 'DN [m]' };
  const nodeLabel = NODE_VAR_LABELS[nodeVariable] || nodeVariable;
  const pipeLabel = PIPE_VAR_LABELS[pipeVariable] || pipeVariable;

  return (
    <PrototypeStudioLayout title="Résultats cartographiques">
      <NemoShell networkName="Discovery PUC CO_Design">
        {/* Optimization header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 8, border: '1px solid var(--nj-core-color-grey-300)', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
            <NJIconButton icon="navigate_before" label="Optimisation précédente" variant="tertiary" scale="sm" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 8, border: '1px solid var(--nj-core-color-grey-300)', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
            <NJIconButton icon="navigate_next" label="Optimisation suivante" variant="tertiary" scale="sm" />
          </div>
          <span className="material-icons nj-icon-material nj-icon-material--md" aria-hidden="true">stacked_bar_chart</span>
          <b style={{ fontSize: 14 }}>Sizing reserve de puissance</b>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <NJBadge variant="success" emphasis="subtle" scale="md">Succès</NJBadge>
          </span>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <NJIconButton icon="table_view" label="Ancienne Version" variant="tertiary" scale="lg" />
            <NJIconButton icon="delete_outline" label="Supprimer" variant="tertiary" scale="lg" />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--nj-core-color-grey-300)', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
          <div style={{ display: 'flex' }}>
            {TABS.map((tab, i) => (
              <a
                key={tab.id}
                href="#"
                onClick={(e) => { e.preventDefault(); setActiveTab(i); }}
                style={{
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                  padding: 'var(--nj-semantic-size-spacing-8, 8px) var(--nj-semantic-size-spacing-24, 24px)',
                  borderBottom: i === activeTab ? '2px solid var(--nj-core-color-blue-500)' : '2px solid transparent',
                }}
              >
                {tab.label}
              </a>
            ))}
          </div>
        </div>

        {/* Toolbar */}
        {data && (
          <Toolbar
            nodeVariable={nodeVariable}
            pipeVariable={pipeVariable}
            nodesVisible={nodesVisible}
            pipesVisible={pipesVisible}
            onNodeVariableChange={setNodeVariable}
            onPipeVariableChange={setPipeVariable}
            onNodesVisibleChange={setNodesVisible}
            onPipesVisibleChange={setPipesVisible}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}

        {/* Map area */}
        <div style={styles.mapContainer}>
          {isLoading && <div style={styles.loading}>Chargement du réseau...</div>}
          {isError && <div style={styles.error}>Erreur de chargement des données</div>}
          {data && (
            <>
              <MapView
                data={data}
                nodeVariable={nodeVariable}
                pipeVariable={pipeVariable}
                nodesVisible={nodesVisible}
                pipesVisible={pipesVisible}
                searchQuery={searchQuery}
              />
              <Legend nodeLabel={nodeLabel} pipeLabel={pipeLabel} nodeVariable={nodeVariable} pipeVariable={pipeVariable} />
            </>
          )}
          {/* Bottom slider */}
          <div style={styles.slider}>
            <span style={{ fontSize: 12, color: '#333' }}>Puissance appelée maximale</span>
            <input type="range" min={-15} max={5} defaultValue={-5} style={{ width: 200 }} disabled />
            <span style={{ fontSize: 12, fontWeight: 700 }}>-5.00°C</span>
          </div>
        </div>
      </NemoShell>
    </PrototypeStudioLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  mapContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  loading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 14,
    color: '#666',
  },
  error: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 14,
    color: '#c62828',
  },
  slider: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(255,255,255,0.95)',
    padding: '8px 16px',
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    zIndex: 10,
  },
};
